import { EyeInvisibleOutlined, EyeOutlined, ShareAltOutlined } from '@ant-design/icons';
import { EvidiaModal } from '@evidia/components';
import { Alert, Button, Space, notification } from 'antd';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { trpc } from '../../utils/trcp';
import Center from '../Center';

const Code = styled.code`
	font-size: 2rem;
`;

const StyledButton = styled(Button)`
	width: 100%;
`;

const StyledMessage = styled.div`
	p,
	ol,
	ul {
		margin: 1em;
	}

	li {
		margin-left: 1em;
	}
`;

const ShareButton = styled(Button)`
	border: none;
	box-shadow: none;

	&:hover {
		border: none;
		background-color: #d2d2d2;
	}
`;

interface Props {
	activeSchoolId: string;
	studentId: string;
}

const StudentPassShareButton: React.FC<Props> = ({ activeSchoolId, studentId }) => {
	const [showCode, setShowCode] = useState(false);
	const [isShareModalOpen, setIsShareModalOpen] = useState(false);

	const sendStudentPassInfo = trpc.student.sendStudentPassInfo.useMutation();
	const getStudentPassShareInfo = trpc.student.getStudentPassShareInfo.useQuery({
		studentId,
		schoolId: activeSchoolId,
	});
	// eslint-disable-next-line max-len
	const passUrl = `${window.location.origin}/student/${activeSchoolId}/${getStudentPassShareInfo.data?.studentRegistrationNumber}`;

	const message = (
		<StyledMessage>
			<p>
				Studentská průkazka je digitální doklad, který slouží k identifikaci studenta, tak jak to vyžaduje
				zákon.
			</p>
			<p>Tato průkazka je dostupná online.</p>
			<ol>
				<li>
					{'Student může zobrazit svou průkazku na adrese '}
					<a href={passUrl} target="_blank" rel="noreferrer">
						{passUrl}
					</a>
					{' po zadání unikátního kódu.'}
				</li>
				<li>
					Sdílení odkazu - Automaticky emailem (pokud je vyplněn e-mail v kartě žáka) - Manuálně přes
					preferované komunikační platformy (např. WhatsApp, Facebook Messenger, SMS)
				</li>
				<li>Kód se skládá ze dvou částí: a) Unikátní kód autoškoly, b) Kód studenta</li>
				<li>Každá část kódu může být z bezpečnostních důvodů kdykoliv změněna.</li>
				<ul>
					<li>
						{'Hromadná regenerace kódu autoškoly: '}
						<Link to="/app/settings/student-pass">nastavení průkazky</Link>
					</li>
					<li>Regenerace kódu studenta: Lze provést přímo v této sekci</li>
				</ul>
			</ol>
			<p>
				Poznámka: Zajistěte bezpečné předání kódu studentovi, aby se zabránilo neoprávněnému přístupu k
				průkazce.
			</p>
		</StyledMessage>
	);

	const button = (
		<ShareButton
			icon={<ShareAltOutlined />}
			onClick={() => setIsShareModalOpen(true)}
			loading={getStudentPassShareInfo.isLoading}
		/>
	);

	if (getStudentPassShareInfo.isLoading) {
		return button;
	}

	const getCode = () => {
		// eslint-disable-next-line max-len
		const code = `${getStudentPassShareInfo.data?.schoolCode}${getStudentPassShareInfo.data?.studentCode}`;
		const onlyStudentCode = `(Kód studenta: ${getStudentPassShareInfo.data?.studentCode})`;
		if (showCode) {
			return (
				<center>
					{code}
					<b>{' (Tento kód předejte studentovi)'}</b>
					<br />
					<br />
					{onlyStudentCode}
				</center>
			);
		}
		return '********';
	};

	return (
		<>
			{button}
			<EvidiaModal
				width="50%"
				title="Sdílet průkazku se studentem"
				open={isShareModalOpen}
				onCancel={() => setIsShareModalOpen(false)}
				cancelText="Zavřít"
				okButtonProps={{ style: { display: 'none' } }}
			>
				<Space direction="vertical" size="middle" style={{ display: 'flex' }}>
					<Alert message={message} type="info" />
					<p>
						<StyledButton
							disabled={!getStudentPassShareInfo.data?.email}
							loading={sendStudentPassInfo.isLoading}
							onClick={async () => {
								await sendStudentPassInfo.mutateAsync({
									schoolId: activeSchoolId,
									studentId,
								});
								notification.success({
									message: 'Průkazka byla odeslána emailem',
								});
							}}
						>
							Odeslat průkazku e-mailem
						</StyledButton>
						{!getStudentPassShareInfo.data?.email && <small>Student nemá vyplněný e-mail.</small>}
					</p>
					<p>
						<StyledButton
							disabled={!getStudentPassShareInfo.data?.email}
							loading={sendStudentPassInfo.isLoading}
							onClick={async () => {
								await sendStudentPassInfo.mutateAsync({
									schoolId: activeSchoolId,
									studentId,
									generateNew: true,
								});
								notification.success({
									message: 'Průkazka byla odeslána emailem',
								});
							}}
						>
							Přegenerovat kód a odeslat průkazku e-mailem
						</StyledButton>
						{!getStudentPassShareInfo.data?.email && <small>Student nemá vyplněný e-mail.</small>}
					</p>
					<div>
						<div>
							<p>Aktuální kód studenta</p>

							<Center>
								<Code>{getCode()}</Code>
							</Center>
							<Center>
								<Button
									shape="circle"
									icon={!showCode ? <EyeOutlined /> : <EyeInvisibleOutlined />}
									onClick={() => setShowCode(!showCode)}
								/>
							</Center>
						</div>
					</div>
					<p>
						{'Odkaz na průkazku: '}
						<a href={passUrl} target="_blank" rel="noreferrer">
							{passUrl}
						</a>
					</p>
				</Space>
			</EvidiaModal>
		</>
	);
};

export default StudentPassShareButton;
